import { Divider, Flex, Input, isNullish, Select, Switch } from "@theorgchartdev/storybook";
import { FieldInputLabel } from "@/appComponents/Blocks/Composites/FieldInputLabel";
import { GetDoc } from "@/Utils/StateLib";
import type { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import type { FieldData } from "./ExcelContent";
import type { FormController } from "@/hooks/useFormController";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import {
  filterExcelScopeOptions,
  hasInvalidFilenameChars,
  isAccessibilityReportAvailable,
  type ScopeOption
} from "../ExportModal.utils";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";

type Props = {
  formController: FormController<FieldData>;
  isTableExport: boolean;
};

export const ExcelGeneral = (props: Props) => {
  const { fieldData, onFieldChange } = props.formController;
  const { hasActiveBLS, showFunctionalStatement } = usePermissionCheck();
  const filenameError = hasInvalidFilenameChars(fieldData.name);
  const showFunctionalStatements = showFunctionalStatement && !props.isTableExport;

  const doc = GetDoc();
  const showAccessibilityReport = isAccessibilityReportAvailable(doc);

  return (
    <Flex direction="column" gap="6px" padding="6px 24px 0">
      <Input
        label={appstr("File Name")}
        data-testid="export-modal-input-name"
        placeholder={`${appstr("Enter")} ${appstr("File Name").toLocaleLowerCase()}`}
        size="medium"
        onChange={(_, value) => onFieldChange("name", value)}
        value={fieldData.name}
        status={filenameError ? "error" : "default"}
        message={filenameError ? appstr("exportFilenameInvalidChars") : undefined}
      />
      {!props.isTableExport && (
        <>
          <Divider />
          <Select
            dataTestId="export-modal-select-scope"
            inputOnly={false}
            selectSize="medium"
            label={appstr("Scope")}
            options={buildScopeOptions(value => onFieldChange("scope", value), hasActiveBLS)}
            value={fieldData.scope}
          />
          <Flex direction="column" gap="12px">
            <FieldInputLabel
              id="excel-chain-of-command-switch"
              label={appstr("includechain")}
              helper={appstr(
                "Include a field that contains the reporting hierarchy for each employee"
              )}
            >
              <Switch
                data-testid="export-modal-switch-chain-of-command"
                id="excel-chain-of-command-switch"
                onChange={nextChecked => onFieldChange("chainOfCommand", nextChecked)}
                checked={fieldData.chainOfCommand}
              />
            </FieldInputLabel>
            <FieldInputLabel
              id="excel-system-calculations-switch"
              label={appstr("Include system calculations")}
              helper={appstr(
                "Include pre-defined system formulas, such as headcount and span of control"
              )}
            >
              <Switch
                id="excel-system-calculations-switch"
                data-testid="export-modal-switch-system-calculations"
                onChange={nextChecked => onFieldChange("systemCalculations", nextChecked)}
                checked={fieldData.systemCalculations}
              />
            </FieldInputLabel>
            {showFunctionalStatements && (
              <FieldInputLabel
                id="excel-functional-statements-switch"
                label={appstr("Include Functional Statements")}
                helper={appstr("includeExcelFunctionalStatementsHelper")}
              >
                <Switch
                  id="excel-functional-statements-switch"
                  data-testid="export-modal-switch-functional-statements"
                  onChange={nextChecked =>
                    onFieldChange("includeFunctionalStatements", nextChecked)
                  }
                  checked={fieldData.includeFunctionalStatements}
                />
              </FieldInputLabel>
            )}
            {showAccessibilityReport && (
              <FieldInputLabel
                id="excel-accessibility-report-switch"
                label={appstr("Accessibility Report")}
                helper={appstr("accessibilityReportHelper")}
              >
                <Switch
                  id="excel-accessibility-report-switch"
                  data-testid="export-modal-switch-accessibility-report"
                  onChange={nextChecked => onFieldChange("accessibilityReport", nextChecked)}
                  checked={fieldData.accessibilityReport}
                />
              </FieldInputLabel>
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
};

const buildScopeOptions = (
  onChange: (value: string) => void,
  hasActiveBLS: boolean
): ListItemOption[] => {
  let disableOption = false;

  const getLabel = (option: ScopeOption) => {
    if (!isNullish(option.labelKey)) {
      return appstr(option.labelKey);
    }

    return appstr(option.value);
  };

  const filteredOptions = filterExcelScopeOptions(GetDoc(), hasActiveBLS);

  return filteredOptions.map((option, index) => {
    if (
      option.value === "toporphans" ||
      option.value === "orphanrecords" ||
      option.value === "orphans"
    ) {
      if (!getFeatureAccess(enumFeaturesList.exportDisconnected)) {
        disableOption = true;
      }
    }

    const isLastOption = index === filteredOptions.length - 1;

    return {
      divider: isLastOption ? false : option.divider,
      dataTestId: `export-modal-select-scope-${option.value}`,
      value: option.value,
      label: getLabel(option),
      clickHandler: () => onChange(option.value),
      disabled: disableOption
    };
  });
};
